import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Settings,
  User,
  Shield,
  HardDrive,
  Palette,
  RefreshCw,
  Building2,
  Users,
  FileText,
  Info,
  Layers,
  Calculator,
  Sparkles,
  Factory,
} from 'lucide-react';
import Layout from '../components/Layout';
import BulkSyncDialog from '../components/BulkSyncDialog';
import TemplateManager from '../components/TemplateManager';
import ProcessTemplateManager from '../components/settings/ProcessTemplateManager';
import { BarStockPriceManager, CostSettingsManager } from '../components/CostEstimation';
import PromptEditorDialog from '../components/PromptEditorDialog';
import authService from '../services/authService';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [bulkSyncDialogOpen, setBulkSyncDialogOpen] = useState(false);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [processTemplateManagerOpen, setProcessTemplateManagerOpen] = useState(false);
  const [barStockManagerOpen, setBarStockManagerOpen] = useState(false);
  const [costSettingsOpen, setCostSettingsOpen] = useState(false);
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  const isAdmin = currentUser?.role === 'admin';
  const isSales = currentUser?.role === 'sales_person';
  const isEngineer = currentUser?.role === 'engineer';
  const isWarehouse = currentUser?.role === 'warehouse_manager';

  const handleBulkSyncClick = () => {
    setBulkSyncDialogOpen(true);
  };

  const handleSuppliersClick = () => {
    navigate('/suppliers');
  };

  const handleClientsClick = () => {
    navigate('/settings/clients');
  };

  const handleTemplateManagerClick = () => {
    setTemplateManagerOpen(true);
  };

  const handleProcessTemplateManagerClick = () => {
    setProcessTemplateManagerOpen(true);
  };

  // Settings card data
  const settingsCards = [
    {
      key: 'profile',
      icon: User,
      iconColor: 'text-primary',
      title: '个人资料设置',
      description: '管理您的账户信息',
      buttonText: '编辑个人资料',
      buttonVariant: 'default',
      onClick: () => {},
      show: true,
    },
    {
      key: 'security',
      icon: Shield,
      iconColor: 'text-success',
      title: '安全设置',
      description: '密码和安全设置',
      buttonText: '安全设置',
      buttonVariant: 'outline',
      onClick: () => {},
      show: true,
    },
    {
      key: 'theme',
      icon: Palette,
      iconColor: 'text-warning',
      title: '主题和显示',
      description: '自定义外观设置',
      buttonText: '自定义',
      buttonVariant: 'outline',
      onClick: () => {},
      show: true,
    },
    {
      key: 'suppliers',
      icon: Building2,
      iconColor: 'text-secondary',
      title: '供应商管理',
      description: '管理供应商信息和关系',
      buttonText: '管理供应商',
      buttonVariant: 'outline',
      onClick: handleSuppliersClick,
      show: true,
    },
    {
      key: 'clients',
      icon: Users,
      iconColor: 'text-primary',
      title: '客户管理',
      description: '管理客户信息和配置',
      buttonText: isAdmin || isWarehouse || isSales ? '管理客户' : '需要权限',
      buttonVariant: 'outline',
      onClick: handleClientsClick,
      show: true,
      disabled: !(isAdmin || isWarehouse || isSales),
    },
    {
      key: 'backup',
      icon: HardDrive,
      iconColor: 'text-info',
      title: '备份和恢复',
      description: '数据备份和恢复选项',
      buttonText: isAdmin ? '批量同步' : '查看备份',
      buttonVariant: 'outline',
      onClick: handleBulkSyncClick,
      show: true,
      disabled: !isAdmin,
      buttonIcon: isAdmin ? RefreshCw : null,
    },
    {
      key: 'templates',
      icon: FileText,
      iconColor: 'text-secondary',
      title: '模板管理',
      description: '管理Excel导出模板',
      buttonText: isAdmin || isWarehouse || isSales ? '管理模板' : '需要权限',
      buttonVariant: 'outline',
      onClick: handleTemplateManagerClick,
      show: true,
      disabled: !(isAdmin || isWarehouse || isSales),
    },
    {
      key: 'process-templates',
      icon: Factory,
      iconColor: 'text-indigo-500',
      title: '工序模板管理',
      description: '管理生产工艺流程模板',
      buttonText: isAdmin || isEngineer ? '管理工序模板' : '需要权限',
      buttonVariant: 'outline',
      onClick: handleProcessTemplateManagerClick,
      show: true,
      disabled: !(isAdmin || isEngineer),
    },
    {
      key: 'bar-stock-prices',
      icon: Layers,
      iconColor: 'text-success',
      title: '棒料价格管理',
      description: '管理硬质合金棒料报价',
      buttonText: isAdmin ? '管理价格' : '查看价格',
      buttonVariant: 'outline',
      onClick: () => setBarStockManagerOpen(true),
      show: true,
      disabled: !isAdmin,
    },
    {
      key: 'cost-settings',
      icon: Calculator,
      iconColor: 'text-warning',
      title: '成本核算配置',
      description: '配置人工、电费、工时参数',
      buttonText: isAdmin ? '配置参数' : '查看配置',
      buttonVariant: 'outline',
      onClick: () => setCostSettingsOpen(true),
      show: true,
      disabled: !isAdmin,
    },
    {
      key: 'prompt-rules',
      icon: Sparkles,
      iconColor: 'text-purple-500',
      title: '货号生成规则',
      description: '配置AI生成产品编号的规则',
      buttonText: isAdmin || isSales || isEngineer ? '编辑规则' : '需要权限',
      buttonVariant: 'outline',
      onClick: () => setPromptEditorOpen(true),
      show: true,
      disabled: !(isAdmin || isSales || isEngineer),
    },
  ];

  return (
    <Layout title="系统设置" activeTab="settings">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            系统设置
          </h1>
          <p className="text-muted-foreground">
            Configure system preferences and manage your account
          </p>
        </div>

        {/* Admin Notice */}
        {isAdmin && (
          <Alert className="mb-6 border-info/50 bg-info/10">
            <Info className="h-4 w-4 text-info" />
            <AlertDescription className="text-info">
              You are logged in as an administrator. You have access to advanced features like bulk data sync.
            </AlertDescription>
          </Alert>
        )}

        {/* Settings Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {settingsCards.map((card) => {
            const IconComponent = card.icon;
            const ButtonIcon = card.buttonIcon;
            return (
              <Card key={card.key} className="border bg-card">
                <CardContent className="pt-6 text-center">
                  <IconComponent className={cn("h-12 w-12 mx-auto mb-4", card.iconColor)} />
                  <h3 className="text-lg font-semibold mb-1">{card.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{card.description}</p>
                  <Button
                    variant={card.buttonVariant}
                    size="sm"
                    onClick={card.onClick}
                    disabled={card.disabled}
                    className={cn(
                      card.buttonVariant === 'default' && 'bg-primary text-primary-foreground',
                    )}
                  >
                    {ButtonIcon && <ButtonIcon className="h-4 w-4 mr-2" />}
                    {card.buttonText}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Coming Soon Message */}
        <Card className="border bg-card">
          <CardContent className="py-12 text-center">
            <Settings className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h2 className="text-2xl font-semibold mb-3 text-foreground">
              设置模块即将推出
            </h2>
            <p className="text-muted-foreground mb-6">
              此页面正在开发中。高级设置和配置选项即将推出。
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              返回仪表板
            </Button>
          </CardContent>
        </Card>

        {/* Bulk Sync Dialog */}
        <BulkSyncDialog
          open={bulkSyncDialogOpen}
          onClose={() => setBulkSyncDialogOpen(false)}
        />

        {/* Template Manager Dialog */}
        <Dialog open={templateManagerOpen} onOpenChange={setTemplateManagerOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                模板管理
              </DialogTitle>
            </DialogHeader>
            <TemplateManager
              dialogMode={true}
              onTemplateSelect={(template) => {
                console.log('Template selected:', template);
                setTemplateManagerOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Process Template Manager Dialog */}
        <Dialog open={processTemplateManagerOpen} onOpenChange={setProcessTemplateManagerOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                工序模板管理
              </DialogTitle>
            </DialogHeader>
            <ProcessTemplateManager />
          </DialogContent>
        </Dialog>

        {/* Bar Stock Price Manager Dialog */}
        <BarStockPriceManager
          open={barStockManagerOpen}
          onClose={() => setBarStockManagerOpen(false)}
        />

        {/* Cost Settings Manager Dialog */}
        <CostSettingsManager
          open={costSettingsOpen}
          onClose={() => setCostSettingsOpen(false)}
        />

        {/* Prompt Editor Dialog */}
        <PromptEditorDialog
          open={promptEditorOpen}
          onClose={() => setPromptEditorOpen(false)}
          promptType="product-id-rules"
          title="货号生成规则配置"
          description="配置AI根据产品名称、规格、材质生成标准货号的规则。保存后立即生效。"
        />
      </div>
    </Layout>
  );
};

export default SettingsPage;
